// src/core.ts
import {
  isArraySubject,
  isObjectSubject,
  isSignal,
  isComputed
} from "@signe/reactive";

// src/utils.ts
function isFunction(val) {
  return {}.toString.call(val) === "[object Function]";
}
function isClass(obj) {
  return typeof obj === "function" && obj.prototype && obj.prototype.constructor === obj;
}
var isObject = (item) => item && typeof item === "object" && !Array.isArray(item) && item !== null;
function isInstanceOfClass(value) {
  if (value === null || typeof value !== "object" || value === void 0 || Array.isArray(value)) {
    return false;
  }
  return Object.getPrototypeOf(value) !== Object.prototype;
}
function generateShortUUID() {
  const chars = "ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789";
  let uuid = "";
  for (let i = 0; i < 8; i++) {
    const randomIndex = Math.floor(Math.random() * chars.length);
    uuid += chars[randomIndex];
  }
  return uuid;
}

// src/core.ts
var DELETE_TOKEN = "$delete";
var syncClass = (instance, options = {}) => {
  const cacheSync = /* @__PURE__ */ new Map();
  const cachePersist = /* @__PURE__ */ new Map();
  instance.$valuesChanges = {
    set: (path, value) => {
      cacheSync.set(path, value);
      options.onSync?.(cacheSync);
    },
    setPersist: (path, value) => {
      if (path == "") path = ".";
      cachePersist.set(path, value);
      options.onPersist?.(cachePersist);
    },
    has: (path) => {
      return cacheSync.has(path);
    },
    get: (path) => {
      return cacheSync.get(path);
    }
  };
  createSyncClass(instance);
};
function createStatesSnapshot(instance) {
  let persistObject = {};
  if (instance?.$snapshot) {
    for (const key of instance.$snapshot.keys()) {
      const signal = instance.$snapshot.get(key);
      const persist2 = signal.options.persist ?? true;
      let value = signal();
      if (isObject(value) || Array.isArray(value)) {
        continue;
      }
      if (persist2) {
        persistObject[key] = value;
      }
    }
  }
  return persistObject;
}
var SNAPSHOT_SKIP = Symbol("snapshot-skip");
var serializeSnapshotDeep = (value, path, options, seen) => {
  if (isSignal(value)) {
    return serializeSnapshotDeep(value(), path, options, seen);
  }
  if (value instanceof Map) {
    return SNAPSHOT_SKIP;
  }
  if (options.filter && !options.filter(value, path)) {
    return SNAPSHOT_SKIP;
  }
  if (value instanceof Date) {
    return options.dateToString ? options.dateToString(value) : value.toISOString();
  }
  if (value && typeof value === "object") {
    if (seen.has(value)) {
      return SNAPSHOT_SKIP;
    }
    seen.add(value);
    if (Array.isArray(value)) {
      const result2 = [];
      value.forEach((item, index) => {
        const itemPath = path ? `${path}.${index}` : String(index);
        const serialized = serializeSnapshotDeep(item, itemPath, options, seen);
        if (serialized !== SNAPSHOT_SKIP) {
          result2.push(serialized);
        }
      });
      return result2;
    }
    const result = {};
    const idKey = isInstanceOfClass(value) ? value.constructor?._propertyMetadata?.get("id") : void 0;
    const entries = Object.entries(value).filter(
      ([key]) => isInstanceOfClass(value) ? key.startsWith("__") || (idKey ? key === idKey : false) : true
    );
    for (const [key, childValue] of entries) {
      const normalizedKey = key.startsWith("__") ? key.slice(2) : key;
      const childPath = path ? `${path}.${normalizedKey}` : normalizedKey;
      const serialized = serializeSnapshotDeep(childValue, childPath, options, seen);
      if (serialized !== SNAPSHOT_SKIP) {
        result[normalizedKey] = serialized;
      }
    }
    return result;
  }
  return value;
};
function createStatesSnapshotDeep(instance, options = {}) {
  const persistObject = {};
  if (instance?.$snapshot) {
    for (const key of instance.$snapshot.keys()) {
      const signal = instance.$snapshot.get(key);
      const persist2 = signal.options.persist ?? true;
      if (!persist2) {
        continue;
      }
      const value = signal();
      const serialized = serializeSnapshotDeep(
        value,
        key,
        options,
        /* @__PURE__ */ new WeakSet()
      );
      if (serialized !== SNAPSHOT_SKIP) {
        persistObject[key] = serialized;
      }
    }
  }
  return persistObject;
}
function setMetadata(target, key, value) {
  const meta = target.constructor._propertyMetadata;
  const propId = meta?.get(key);
  if (propId) {
    if (isSignal(target[propId])) {
      target[propId].set(value);
    } else {
      target[propId] = value;
    }
  }
}
var createSyncClass = (currentClass, parentKey = null, parentClass = null, path = "") => {
  currentClass.$path = path;
  if (parentClass) {
    currentClass.$valuesChanges = parentClass.$valuesChanges;
  }
  if (parentKey) {
    setMetadata(currentClass, "id", parentKey);
  }
  applyDecoratedSignalMetadata(currentClass);
  if (currentClass.$snapshot) {
    for (const key of currentClass.$snapshot.keys()) {
      const signal = currentClass.$snapshot.get(key);
      const syncToClient = signal.options?.syncToClient ?? true;
      const persist2 = signal.options?.persist ?? true;
      const transform = signal.options?.transform;
      let signalValue = signal();
      if (transform) {
        signalValue = transform(signalValue);
      }
      if (isObject(signalValue) || Array.isArray(signalValue)) {
        signalValue = { ...signalValue };
      }
      const transformedValue = signalValue;
      const newPath = (path ? path + "." : "") + key;
      if (syncToClient) {
        currentClass.$valuesChanges.set(newPath, transformedValue);
      }
      if (persist2) {
        if (parentClass)
          currentClass.$valuesChanges.setPersist(path, transformedValue);
      }
      if (isComputed(signal)) {
        signal.observable.subscribe((newValue) => {
          if (syncToClient) {
            const transformedNewValue = transform ? transform(newValue) : newValue;
            currentClass.$valuesChanges.set(newPath, transformedNewValue);
          }
        });
      }
    }
  }
};
function applyDecoratedSignalMetadata(currentClass) {
  const syncMetadata = currentClass.constructor?._syncMetadata;
  if (!syncMetadata) {
    return;
  }
  for (const [propertyKey, options] of syncMetadata) {
    const signal = currentClass[propertyKey];
    if (!isSignal(signal) || signal.options) {
      continue;
    }
    currentClass[propertyKey] = type(
      signal,
      propertyKey,
      { ...options, skipInitialSync: true },
      currentClass
    );
  }
}
var type = (_signal, path, options = {}, currentInstance) => {
  const { syncToClient = true, persist: persist2 = true, transform, skipInitialSync = false } = options;
  let init = true;
  const handleObjectSubject = (value, propPath) => {
    const newPath = `${propPath}${value.key ? `.${value.key}` : ""}`;
    if (["add", "reset", "update"].includes(value.type)) {
      if (isInstanceOfClass(value.value)) {
        createSyncClass(value.value, value.key, currentInstance, newPath);
      } else if (value.type === "update" && (isObject(value.value) || Array.isArray(value.value))) {
        createSyncClass(value.value, value.key, currentInstance, newPath);
      } else {
        savePath(newPath, value.value);
      }
    } else if (value.type === "remove") {
      savePath(newPath, DELETE_TOKEN);
    }
  };
  const handleArraySubject = (value, propPath) => {
    if (value.type === "reset" && Array.isArray(value.items)) {
      value.items.forEach((item, index) => {
        const newPath2 = `${propPath}.${index}`;
        if (isInstanceOfClass(item)) {
          createSyncClass(item, value.key, currentInstance, newPath2);
        } else {
          savePath(newPath2, item);
        }
      });
      return;
    }
    const newPath = `${propPath}.${value.index}`;
    const firstItem = value.items?.[0];
    if (["add", "update"].includes(value.type) && firstItem !== void 0) {
      if (isInstanceOfClass(firstItem)) {
        createSyncClass(firstItem, value.key, currentInstance, newPath);
      } else if (value.type === "update" && (isObject(firstItem) || Array.isArray(firstItem))) {
        createSyncClass(firstItem, value.key, currentInstance, newPath);
      } else {
        savePath(newPath, firstItem);
      }
    } else if (value.type === "remove") {
      savePath(newPath, DELETE_TOKEN);
    }
  };
  const savePath = (propPath, value) => {
    const transformedValue = transform && value !== DELETE_TOKEN ? transform(value) : value;
    if (syncToClient) {
      currentInstance.$valuesChanges.set(propPath, transformedValue);
    }
    if (persist2 && currentInstance.$path !== void 0) {
      currentInstance.$valuesChanges.setPersist(
        propPath,
        transformedValue
      );
    }
  };
  const setupSubscription = (signal, signalPath) => {
    if (!isSignal(signal)) return;
    if (syncToClient && !skipInitialSync && currentInstance.$valuesChanges) {
      const initialValue = signal();
      const transformedInitialValue = transform ? transform(initialValue) : initialValue;
      const initialPath = currentInstance.$path !== void 0 ? `${currentInstance.$path ? `${currentInstance.$path}.` : ""}${signalPath}` : signalPath;
      currentInstance.$valuesChanges.set(initialPath, transformedInitialValue);
    }
    signal.options = options;
    signal.observable.subscribe((value) => {
      if (init) return;
      if (currentInstance.$path !== void 0) {
        const fullPath = `${currentInstance.$path ? currentInstance.$path + "." : ""}${signalPath}`;
        if (isComputed(signal)) {
          savePath(fullPath, value);
        } else if (isObjectSubject(signal._subject)) {
          handleObjectSubject(value, fullPath);
        } else if (isArraySubject(signal._subject)) {
          handleArraySubject(value, fullPath);
        } else {
          savePath(fullPath, value);
        }
      }
    });
    if (!currentInstance.$snapshot) {
      currentInstance.$snapshot = /* @__PURE__ */ new Map();
    }
    currentInstance.$snapshot.set(path, signal);
  };
  if (!isSignal(_signal)) {
    if (_signal && typeof _signal === "object" && !Array.isArray(_signal)) {
      for (const key in _signal) {
        if (Object.prototype.hasOwnProperty.call(_signal, key)) {
          const value = _signal[key];
          const propertyPath = `${path}.${key}`;
          if (isSignal(value)) {
            setupSubscription(value, propertyPath);
          } else if (value && typeof value === "object" && !Array.isArray(value)) {
            type(value, propertyPath, options, currentInstance);
          }
        }
      }
      init = false;
    }
    return _signal;
  }
  setupSubscription(_signal, path);
  init = false;
  return _signal;
};

// src/decorators.ts
function normalizeSyncOptions(options) {
  let classType;
  let persist2 = true;
  let syncToClient = true;
  let transform;
  if (typeof options === "function") {
    classType = options;
  } else if (typeof options === "object") {
    classType = options.classType;
    if (options.hasOwnProperty("persist")) {
      persist2 = options.persist;
    }
    if (options.hasOwnProperty("syncToClient")) {
      syncToClient = options.syncToClient;
    }
    if (options.hasOwnProperty("transform")) {
      transform = options.transform;
    }
  }
  return { classType, persist: persist2, syncToClient, transform };
}
function setSyncMetadata(target, propertyKey, options) {
  if (!target.constructor._syncMetadata) {
    target.constructor._syncMetadata = /* @__PURE__ */ new Map();
  }
  target.constructor._syncMetadata.set(propertyKey, options);
}
function sync(options) {
  const normalizedOptions = normalizeSyncOptions(options);
  return function(target, propertyKey) {
    setSyncMetadata(target, propertyKey, normalizedOptions);
    const privatePropertyKey = `__${propertyKey}`;
    const getter = function() {
      return this[privatePropertyKey];
    };
    const setter = function(newVal) {
      this[privatePropertyKey] = type(
        newVal,
        propertyKey,
        normalizedOptions,
        this
      );
    };
    Object.defineProperty(target, propertyKey, {
      get: getter,
      set: setter,
      enumerable: true,
      configurable: true
    });
  };
}
function id() {
  return function(target, propertyKey) {
    if (!target.constructor._propertyMetadata) {
      target.constructor._propertyMetadata = /* @__PURE__ */ new Map();
    }
    target.constructor._propertyMetadata.set("id", propertyKey);
  };
}
function users(options) {
  return function(target, propertyKey) {
    if (!target.constructor._propertyMetadata) {
      target.constructor._propertyMetadata = /* @__PURE__ */ new Map();
    }
    target.constructor._propertyMetadata.set("users", propertyKey);
    sync(options)(target, propertyKey);
  };
}
function persist() {
  return sync({
    persist: true,
    syncToClient: false
  });
}
function connected() {
  return function(target, propertyKey) {
    if (!target.constructor._propertyMetadata) {
      target.constructor._propertyMetadata = /* @__PURE__ */ new Map();
    }
    target.constructor._propertyMetadata.set("connected", propertyKey);
    sync({
      persist: false
    })(target, propertyKey);
  };
}

// src/load.ts
import { isSignal as isSignal2 } from "@signe/reactive";
function load(rootInstance, values, valueIsObject) {
  if (valueIsObject) {
    loadFromObject(rootInstance, values);
  } else {
    loadFromPaths(rootInstance, values);
  }
}
function loadFromPaths(rootInstance, values) {
  for (const [path, value] of Object.entries(values)) {
    const parts = path.split(".");
    loadValue(rootInstance, parts, value);
  }
}
function loadFromObject(rootInstance, values, currentPath = "") {
  for (let key in values) {
    const value = values[key];
    const newPath = currentPath ? `${currentPath}.${key}` : key;
    if (typeof value === "object" && !Array.isArray(value) && value !== null) {
      loadFromObject(rootInstance, value, newPath);
    } else {
      const parts = newPath.split(".");
      loadValue(rootInstance, parts, value);
    }
  }
}
function loadValue(rootInstance, parts, value) {
  let current = rootInstance;
  for (let i = 0; i < parts.length; i++) {
    const part = parts[i];
    if (i === parts.length - 1) {
      if (value == DELETE_TOKEN) {
        if (isSignal2(current)) {
          current = current();
        }
        Reflect.deleteProperty(current, part);
      } else if (current[part]?._subject) {
        current[part].set(value);
      } else if (isSignal2(current) && Array.isArray(current()) && !isNaN(Number(part))) {
        current()[Number(part)] = value;
      } else {
        current[part] = value;
      }
    } else {
      if (isSignal2(current)) {
        current = current();
      }
      const currentValue = current[part];
      if (currentValue === void 0) {
        const parentInstance = getByPath(
          rootInstance,
          parts.slice(0, i).join(".")
        );
        const classType = parentInstance?.options?.classType;
        if (classType) {
          current[part] = !isClass(classType) ? classType(part) : new classType();
          setMetadata(current[part], "id", part);
        } else {
          current[part] = {};
        }
      }
      current = current[part];
    }
  }
}
function getByPath(root, path) {
  const parts = path.split(".");
  let current = root;
  for (const part of parts) {
    if (isSignal2(current)) {
      current = current();
    }
    if (current[part]) {
      current = current[part];
    } else {
      return void 0;
    }
  }
  return current;
}
export {
  DELETE_TOKEN,
  connected,
  createStatesSnapshot,
  createStatesSnapshotDeep,
  createSyncClass,
  generateShortUUID,
  getByPath,
  id,
  isClass,
  isFunction,
  isInstanceOfClass,
  isObject,
  load,
  normalizeSyncOptions,
  persist,
  setMetadata,
  sync,
  syncClass,
  type,
  users
};
//# sourceMappingURL=index.js.map